import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
  useRef,
} from "react";
import axios from "axios";

import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  Stack,
  Paper,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";

import {
  Typography,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Tooltip,
  Skeleton,
  Link,
  Alert,
} from "@mui/material";

import {
  Button,
  IconButton,
  TextField,
  MenuItem,
  Tabs,
  Tab,
  Snackbar,
  CircularProgress,
} from "@mui/material";

import { useTheme, alpha } from "@mui/material/styles";

import AllInboxOutlinedIcon from "@mui/icons-material/AllInboxOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import AutoAwesomeMotionOutlinedIcon from "@mui/icons-material/AutoAwesomeMotionOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DataObjectOutlinedIcon from "@mui/icons-material/DataObjectOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import FullscreenExitOutlinedIcon from "@mui/icons-material/FullscreenExitOutlined";
import FullscreenOutlinedIcon from "@mui/icons-material/FullscreenOutlined";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import MiscellaneousServicesOutlinedIcon from "@mui/icons-material/MiscellaneousServicesOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import PriceChangeOutlinedIcon from "@mui/icons-material/PriceChangeOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import SafetyCheckOutlinedIcon from "@mui/icons-material/SafetyCheckOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import StraightenOutlinedIcon from "@mui/icons-material/StraightenOutlined";
import StyleOutlinedIcon from "@mui/icons-material/StyleOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import TagOutlinedIcon from "@mui/icons-material/TagOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import ViewAgendaOutlinedIcon from "@mui/icons-material/ViewAgendaOutlined";

import { AuthContext } from "../../../../helpers/AuthContext";
import { UIStatusChip } from "../../../../components/ui";

const QTY_INFINITY = 2_147_483_647;

const TABS = {
  OVERVIEW: "overview",
  PRICING: "pricing",
  IMPRINT: "imprint",
  SHIPPING: "shipping",
  SAFETY: "safety",
};

const normalizeList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return [value].filter(Boolean);
};

const safeStr = (value) => {
  if (value == null) return "";
  return String(value).trim();
};

const firstNonEmpty = (...values) => {
  for (const v of values) {
    const s = safeStr(v);
    if (s) return s;
  }
  return "";
};

const isUnverifiedScalarPrice = (price) =>
  price?.PricingDisplay?.source === "synthesized_scalar" &&
  price?.PricingDisplay?.quantityVerified === false;

export const resolveProductPopupDetailId = ({
  productId,
  lookupType,
  initialProduct,
}) => {
  if (productId) return productId;
  if (!["catalog", "promoworld"].includes(String(lookupType || "").toLowerCase())) {
    return null;
  }
  return initialProduct?.Id ?? initialProduct?.id ?? null;
};

export const buildProductPopupApiUrl = (baseUrl, endpoint) => {
  const base = String(baseUrl || "").trim().replace(/\/+$/, "");
  const path = String(endpoint || "").trim().replace(/^\/+/, "");
  return base ? `${base}/${path}` : `/${path}`;
};

export const buildProductPopupPriceRows = (product) => {
  if (!product) return [];

  if (product?.Variants?.length) {
    return product.Variants.flatMap((variant) =>
      (variant?.Prices || []).map((price, idx) => ({
        key: `${variant?.Id || variant?.Number || idx}-${idx}`,
        variant: variant?.Name || "Variant",
        sku: resolveVariantSku(variant, product),
        label: composeVariantLabel(variant, product),
        from: price?.Quantity?.From,
        to: price?.Quantity?.To,
        price: price?.Price,
        isReferencePrice: isUnverifiedScalarPrice(price),
      })),
    );
  }

  return (product?.Prices || []).map((price, idx) => ({
    key: idx,
    variant: product?.Name || "Product",
    sku: product?.Number || "—",
    label: "Base",
    from: price?.Quantity?.From,
    to: price?.Quantity?.To,
    price: price?.Price,
    isReferencePrice: isUnverifiedScalarPrice(price),
  }));
};

const detectCurrencyCode = (product) => {
  if (!product) return "USD";
  return (
    product.currency ||
    product.Currency ||
    (Array.isArray(product.Currencies)
      ? product.Currencies.find(Boolean)
      : null) ||
    (Array.isArray(product.Prices) ? product.Prices[0]?.CurrencyCode : null) ||
    (Array.isArray(product.Variants)
      ? product.Variants.find(
          (v) => Array.isArray(v?.Prices) && v.Prices[0]?.CurrencyCode,
        )?.Prices?.[0]?.CurrencyCode
      : null) ||
    "USD"
  );
};

const formatMoney = (amount, currencyCode = "USD") => {
  if (amount == null || Number.isNaN(Number(amount))) return "—";
  try {
    return new Intl.NumberFormat("en", {
      style: "currency",
      currency: currencyCode,
      currencyDisplay: "narrowSymbol",
      maximumFractionDigits: 2,
    }).format(Number(amount));
  } catch {
    return `${currencyCode} ${Number(amount).toFixed(2)}`;
  }
};

const buildImageUrl = (path, lookupType) => {
  if (!path) return null;

  const raw = String(path).trim();
  if (!raw) return null;

  const catalogLookup = ["promoworld", "catalog"].includes(
    String(lookupType || "").toLowerCase(),
  );

  if (lookupType === "custom" || catalogLookup || raw.startsWith("http")) {
    return raw;
  }

  return `https://api.asicentral.com/v1/${raw}?size=normal`;
};

const getAvailableColors = (product) => {
  const fromAttrs =
    product?.Attributes?.Colors?.Values?.map((c) => c?.Name).filter(Boolean) ||
    [];
  const fromVariants =
    product?.Variants?.map((v) => v?.Color).filter(Boolean) || [];
  const fromSku =
    product?.Sku?.flatMap((s) =>
      (s?.Values || []).filter((v) => v?.Code === "PRCL").map((v) => v?.Name),
    ).filter(Boolean) || [];
  return Array.from(new Set([...fromAttrs, ...fromVariants, ...fromSku]));
};

const getAvailableSizes = (product) => {
  const fromAttrs =
    product?.Attributes?.Sizes?.Values?.map((s) => s?.Name).filter(Boolean) ||
    [];
  const fromVariants =
    product?.Variants?.map((v) => v?.Size).filter(Boolean) || [];
  const fromSku =
    product?.Sku?.flatMap((s) =>
      (s?.Values || []).filter((v) => v?.Code === "SIZE").map((v) => v?.Name),
    ).filter(Boolean) || [];
  return Array.from(new Set([...fromAttrs, ...fromVariants, ...fromSku]));
};

const composeVariantLabel = (variant, product) => {
  const parts = [];
  if (variant?.Color) parts.push(variant.Color);
  if (variant?.Size) parts.push(variant.Size);

  if (!parts.length && Array.isArray(product?.Sku)) {
    const matchedSku = product.Sku.find((s) => s?.SKU === variant?.Number);
    if (matchedSku?.Values?.length) {
      const color = matchedSku.Values.find((v) => v?.Code === "PRCL")?.Name;
      const size = matchedSku.Values.find((v) => v?.Code === "SIZE")?.Name;
      if (color) parts.push(color);
      if (size) parts.push(size);
    }
  }

  return parts.join(", ");
};

const resolveVariantSku = (variant, product) => {
  if (variant?.Number) return variant.Number;
  const match = product?.Sku?.find((s) => {
    const vals = s?.Values || [];
    const size = vals.find((v) => v?.Code === "SIZE")?.Name;
    const color = vals.find((v) => v?.Code === "PRCL")?.Name;
    return (
      (variant?.Size ? size === variant.Size : true) &&
      (variant?.Color ? color === variant.Color : true)
    );
  });
  return match?.SKU || "—";
};

const calculateImprintCharges = (charges, quantity, methodCode) => {
  if (!charges?.length || !methodCode) return 0;

  const applicable = charges
    .filter(
      (c) =>
        c?.TypeCode === methodCode ||
        c?.TypeCode === "RNCH" ||
        c?.TypeCode === "OTCH",
    )
    .flatMap((c) => c?.Prices || []);

  return applicable.reduce((total, price) => {
    const from = price?.Quantity?.From ?? 0;
    const to = price?.Quantity?.To ?? QTY_INFINITY;
    if (from <= quantity && (to === QTY_INFINITY || to >= quantity)) {
      return total + Number(price?.Price || 0);
    }
    return total;
  }, 0);
};

const isCatalogLookup = (lookupType) => ["promoworld", "catalog"].includes(String(lookupType || "").toLowerCase());

const resolveProductImageUrl = (imageUrl, lookupType) => {
  if (!imageUrl) return "";
  if (lookupType === "custom" || isCatalogLookup(lookupType) || String(imageUrl).startsWith("http")) return imageUrl;
  return `https://api.asicentral.com/v1/${imageUrl}?size=normal`;
};


const decodeHtmlEntities = (value) => {
  if (typeof value !== "string") return value || "";
  return value
    .replace(/&quot;/g, '"')
    .replace(/&#34;/g, '"')
    .replace(/&amp;/g, "&")
    .replace(/&#38;/g, "&")
    .replace(/&apos;/g, "'")
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&nbsp;/g, " ");
};

const normalizeColorHex = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return { ColorHex: null, ColorHexes: [] };
  const ColorHexes = Array.from(new Set(raw.split("|")
    .map(part => part.trim())
    .map(part => part.startsWith("#") ? part : `#${part}`)
    .filter(part => /^#[0-9a-fA-F]{6}$/.test(part))
    .map(part => part.toUpperCase())));
  return { ColorHex: ColorHexes[0] || null, ColorHexes };
};

const getInventoryLabel = (inventory = {}) => {
  if (inventory?.isExactQuantity && inventory.quantityAvailable != null) return `${inventory.quantityAvailable} available`;
  if (inventory?.statusLabel) return inventory.statusLabel;
  if (inventory?.isAvailable === true) return "Available";
  if (inventory?.isAvailable === false) return "Unavailable";
  return "Stock unknown";
};

const formatShippingRange = (range) => {
  if (!range || range.min == null) return "";
  const value = Number(range.min) === Number(range.max) ? String(range.min) : `${range.min} - ${range.max}`;
  return [value, range.unit].filter(Boolean).join(" ");
};

const hasMeaningfulData = (value) => {
  if (value == null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return Number.isFinite(value);
  if (Array.isArray(value)) return value.some(hasMeaningfulData);
  if (typeof value === "object") return Object.values(value).some(hasMeaningfulData);
  return Boolean(value);
};

const isLikelyImageUrl = (url, lookupType) => {
  const raw = String(url || "").trim();
  if (!raw) return false;
  if (!isCatalogLookup(lookupType) && !raw.startsWith("http")) return true;

  let parsed;
  try {
    parsed = new URL(raw, typeof window !== "undefined" ? window.location.origin : "http://localhost");
  } catch {
    return false;
  }

  const pathname = parsed.pathname.toLowerCase();
  if (/\.(pdf|docx?|xlsx?|csv|txt|html?|aspx?|php)$/i.test(pathname)) return false;
  if (/(size[-_ ]?chart|spec[-_ ]?sheet|catalog|landing|download)/i.test(raw) && !/\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(pathname)) return false;
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(pathname) || /(image|img|photo|cdn|media|assets|product)/i.test(pathname);
};

const getValidProductImages = (product, lookupType) => {
  const candidates = [
    // ImageUrl is the canonical full-size choice selected by catalogue import.
    // Keep it ahead of Images, which may contain supplier thumbnails or swatches.
    product?.ImageUrl,
    ...(Array.isArray(product?.Images) ? product.Images : []),
    ...(isCatalogLookup(lookupType) && Array.isArray(product?.Variants)
      ? product.Variants.map(variant => variant?.ImageUrl)
      : []),
  ];
  const seen = new Set();
  return candidates
    .filter(url => isLikelyImageUrl(url, lookupType))
    .map(url => resolveProductImageUrl(url, lookupType))
    .filter(url => {
      const key = String(url || "").trim();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const getProductResources = (product, lookupType) => {
  if (!isCatalogLookup(lookupType)) return [];
  return uniqueStrings([
    ...(Array.isArray(product?.Resources) ? product.Resources : []),
    ...(Array.isArray(product?.Images) ? product.Images : [])
      .filter(url => hasMeaningfulData(url) && !isLikelyImageUrl(url, lookupType) && String(url).startsWith("http")),
  ]);
};

const asArray = (value) => Array.isArray(value) ? value : hasMeaningfulData(value) ? [value] : [];
const uniqueStrings = (values) => Array.from(new Set(values.map(v => String(v || "").trim()).filter(Boolean)));

const extractNamedValues = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return uniqueStrings(value.flatMap(extractNamedValues));
  if (typeof value === "string" || typeof value === "number") return [String(value)];
  if (Array.isArray(value.Values)) return extractNamedValues(value.Values);
  return [value.Name, value.name, value.Value, value.value, value.Label, value.label].filter(Boolean).map(String);
};

const getCatalogCategories = (product) => uniqueStrings(extractNamedValues(product?.Categories));
const getCatalogColors = (product) => uniqueStrings([
  ...asArray(product?.Colors),
  ...(product?.Variants || []).map(v => v?.Color),
  ...extractNamedValues(product?.Attributes?.Colors),
]);
const getCatalogSizes = (product) => uniqueStrings([
  ...asArray(product?.Sizes),
  ...(product?.Variants || []).map(v => v?.Size),
  ...extractNamedValues(product?.Attributes?.Sizes),
]);

const normalizePriceBreak = (priceBreak = {}) => {
  const quantity = priceBreak.Quantity || {};
  const from = Number(quantity.From ?? priceBreak.QuantityFrom ?? priceBreak.From ?? 1);
  const toRaw = quantity.To ?? priceBreak.QuantityTo ?? priceBreak.To ?? 2147483647;
  const to = Number(toRaw);
  return {
    Cost: priceBreak.Cost != null ? Number(priceBreak.Cost) : null,
    Price: priceBreak.Price != null ? Number(priceBreak.Price) : null,
    CurrencyCode: priceBreak.CurrencyCode || priceBreak.Currency || "CAD",
    Quantity: {
      From: Number.isFinite(from) ? from : 1,
      To: Number.isFinite(to) ? to : 2147483647,
      $index: quantity.$index ?? priceBreak.$index,
    },
    DiscountCode: priceBreak.DiscountCode || priceBreak.Discount || "",
    IsQUR: Boolean(priceBreak.IsQUR),
  };
};

const sortPriceBreaks = (prices = []) => [...prices].sort((a, b) => (a?.Quantity?.From ?? 0) - (b?.Quantity?.From ?? 0));

const getProductPriceGrid = (product) => {
  if (Array.isArray(product?.Prices) && product.Prices.length) return product.Prices.map(normalizePriceBreak);
  if (product?.Price) return [normalizePriceBreak(product.Price)];
  if (product?.LowestPrice || product?.HighestPrice) return [product.LowestPrice, product.HighestPrice].filter(Boolean).map(normalizePriceBreak);
  return [];
};

const getVariantPrices = (variant, product) => {
  if (Array.isArray(variant?.Prices) && variant.Prices.length) return sortPriceBreaks(variant.Prices.map(normalizePriceBreak));
  if (variant?.Price != null || variant?.Cost != null || variant?.CurrencyCode) {
    return [normalizePriceBreak({
      Cost: variant.Cost,
      Price: variant.Price,
      CurrencyCode: variant.CurrencyCode,
      Quantity: { From: 1, To: 2147483647, $index: 1 },
    })];
  }
  return sortPriceBreaks(getProductPriceGrid(product));
};

const getVariantPriceGrid = getVariantPrices;

const priceGridSignature = (prices) => JSON.stringify((prices || []).map(price => ({
  from: price?.Quantity?.From ?? 1,
  to: price?.Quantity?.To ?? 2147483647,
  cost: price?.Cost ?? null,
  price: price?.Price ?? null,
  currency: price?.CurrencyCode || "",
  discount: price?.DiscountCode || "",
})));

const groupVariantsByPriceGrid = (variants, product) => {
  const groups = new Map();
  (variants || []).forEach((variant) => {
    const prices = getVariantPriceGrid(variant, product);
    const signature = priceGridSignature(prices);
    if (!groups.has(signature)) groups.set(signature, { signature, prices, variants: [] });
    groups.get(signature).variants.push(variant);
  });
  return Array.from(groups.values());
};

const formatQuantityRange = (quantity = {}) => {
  const from = quantity.From ?? 1;
  const to = quantity.To ?? 2147483647;
  if (Number(to) === 2147483647) return `${from}+`;
  return `${from} - ${to}`;
};

const hasPricingData = (product) => {
  if (getProductPriceGrid(product).length) return true;
  return (product?.Variants || []).some(variant => getVariantPriceGrid(variant, product).length);
};
const hasImprintData = (product) => hasMeaningfulData(product?.Imprinting) || hasMeaningfulData(product?.Decoration) || hasMeaningfulData(product?.Decorations);

const getShippingRows = (product) => {
  const summary = product?.ShippingSummary || {};
  const rows = [];
  if (Array.isArray(summary.casePackQtyValues) && summary.casePackQtyValues.length) {
    rows.push({ label: "Case pack", value: summary.casePackQtyValues.join(", ") });
  }
  if (summary.weightRange) rows.push({ label: "Weight", value: formatShippingRange(summary.weightRange) });
  if (summary.volumeRange) rows.push({ label: "Volume", value: formatShippingRange(summary.volumeRange) });
  if (rows.length) return rows.filter(row => hasMeaningfulData(row.value));

  const customData = product?.CustomData || {};
  const shippingSource = customData.shippingSource || {};
  const variants = product?.Variants || [];
  const firstMeaningful = (...values) => values.flat().find(v => hasMeaningfulData(v));
  const formatValueUnit = (value, unit) => [value, unit].filter(hasMeaningfulData).join(" ");

  const casePackValues = uniqueStrings([...(customData.casePackQtyValues || []), ...variants.map(v => v?.Shipping?.casePackQty ?? v?.CustomData?.CasePackQty)]);
  const weight = firstMeaningful(shippingSource.weightValues, customData.weightValues, customData.Weight);
  const weightUnit = firstMeaningful(shippingSource.weightUnits, customData.weightUnits, customData.WeightUnit);
  const volume = firstMeaningful(shippingSource.volumeValues, customData.volumeValues, customData.Volume);
  const volumeUnit = firstMeaningful(shippingSource.volumeUnits, customData.volumeUnits, customData.VolumeUnit);
  const dimensions = firstMeaningful(customData.dimensions, customData.Dimensions, product?.Shipping?.Dimensions, product?.Dimensions);
  const packaging = firstMeaningful(customData.packaging, customData.Packaging, product?.Packaging);
  const leadTime = firstMeaningful(customData.leadTime, customData.LeadTime, product?.ProductionTime, product?.LeadTime);
  const origin = firstMeaningful(product?.Origin, customData.countryOfOrigin, customData.CountryOfOrigin);

  return [
    casePackValues.length && { label: "Case pack", value: casePackValues.join(", ") },
    weight && { label: "Weight", value: formatValueUnit(weight, weightUnit) },
    volume && { label: "Volume", value: formatValueUnit(volume, volumeUnit) },
    dimensions && { label: "Dimensions", value: Array.isArray(dimensions) ? dimensions.join(", ") : dimensions },
    packaging && { label: "Packaging", value: Array.isArray(packaging) ? extractNamedValues(packaging).join(", ") : packaging },
    leadTime && { label: "Lead time", value: Array.isArray(leadTime) ? leadTime.map(v => v?.Name || v?.Description || v?.Days || v).join(", ") : leadTime },
    origin && { label: "Country of origin", value: Array.isArray(origin) ? origin.join(", ") : origin },
  ].filter(row => row && hasMeaningfulData(row.value));
};

const hasProductionShippingData = (product) => getShippingRows(product).length > 0;
const hasSafetyComplianceData = (product) => hasMeaningfulData(product?.Warnings) || hasMeaningfulData(product?.Prop65AdditionalInfo) || hasMeaningfulData(product?.Certifications) || hasMeaningfulData(product?.Disclaimer) || hasMeaningfulData(product?.Compliance);

const hasCatalogProductDetailData = (product, lookupType) => hasMeaningfulData(product?.Name)
  || getValidProductImages(product, lookupType).length > 0
  || hasMeaningfulData(product?.Description)
  || hasMeaningfulData(product?.ShortDescription)
  || getCatalogColors(product).length > 0
  || getCatalogSizes(product).length > 0
  || getCatalogCategories(product).length > 0
  || hasMeaningfulData(product?.Supplier)
  || hasMeaningfulData(product?.SupplierName)
  || hasMeaningfulData(product?.HasInventory)
  || (product?.Variants || []).length > 0;

const getVisibleTabs = (product, lookupType) => {
  const allTabs = [
    { key: "product-detail", label: "Product Detail" },
    { key: "pricing", label: "Pricing" },
    { key: "imprint", label: "Imprint" },
    { key: "production-shipping", label: "Production & Shipping" },
    { key: "safety-compliance", label: "Safety & Compliance" },
  ];

  if (!isCatalogLookup(lookupType)) return allTabs;

  return allTabs.filter(tab => {
    if (tab.key === "product-detail") return hasCatalogProductDetailData(product, lookupType);
    if (tab.key === "pricing") return hasPricingData(product);
    if (tab.key === "imprint") return hasImprintData(product);
    if (tab.key === "production-shipping") return hasProductionShippingData(product);
    if (tab.key === "safety-compliance") return hasSafetyComplianceData(product);
    return false;
  });
};

const formatQtyRange = (from, to) => {
  const lower = from ?? "N/A";
  const upper = to === QTY_INFINITY ? "∞" : to ?? "N/A";
  return `${lower} – ${upper}`;
};

export const getProductPopupPricePresentation = (row = {}) => ({
  quantity: row.isReferencePrice
    ? "Not confirmed"
    : formatQtyRange(row.from, row.to),
  amountLabel: row.isReferencePrice ? "Reference Price" : null,
});

const SectionHeader = ({ icon: Icon, label }) => (
  <Box sx={{ mb: 1.5 }}>
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
      {Icon && <Icon sx={{ fontSize: 14, color: "text.disabled" }} />}
      <Typography
        variant="overline"
        sx={{ color: "text.secondary", letterSpacing: "0.08em", lineHeight: 1 }}
      >
        {label}
      </Typography>
    </Stack>
    <Divider />
  </Box>
);

const FieldRow = ({
  label,
  value,
  multiline = false,
  mono = false,
  onCopy,
}) => {
  const [hovered, setHovered] = useState(false);

  if (value == null || value === "" || value === false) return null;

  const valueNode =
    typeof value === "string" || typeof value === "number" ? (
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        onMouseEnter={() => onCopy && setHovered(true)}
        onMouseLeave={() => onCopy && setHovered(false)}
        sx={{ cursor: onCopy ? "pointer" : "default" }}
        onClick={onCopy ? () => onCopy(String(value)) : undefined}
      >
        <Typography
          variant="body2"
          sx={{
            fontFamily: mono ? "monospace" : "inherit",
            fontWeight: mono ? 500 : 400,
            wordBreak: "break-word",
            lineHeight: 1.5,
            color: "text.primary",
          }}
        >
          {value}
        </Typography>
        {onCopy && (
          <ContentCopyOutlinedIcon
            sx={{
              fontSize: 13,
              color: hovered ? "primary.main" : "text.disabled",
              opacity: hovered ? 1 : 0,
              transition: "opacity 0.15s ease, color 0.15s ease",
              flexShrink: 0,
            }}
          />
        )}
      </Stack>
    ) : (
      <Box sx={{ wordBreak: "break-word" }}>{value}</Box>
    );

  if (multiline) {
    return (
      <Box sx={{ py: 0.5 }}>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", display: "block", mb: 0.35 }}
        >
          {label}
        </Typography>
        <Box
          sx={{ typography: "body2", color: "text.primary", lineHeight: 1.6 }}
        >
          {valueNode}
        </Box>
      </Box>
    );
  }

  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      sx={{ py: 0.45, minHeight: 28 }}
    >
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          minWidth: "38%",
          maxWidth: "38%",
          pr: 1.5,
          flexShrink: 0,
          lineHeight: 1.5,
          fontSize: "0.8125rem",
        }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>{valueNode}</Box>
    </Stack>
  );
};

const KpiTile = ({ icon: Icon, label, value, tone = "neutral" }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      px: 1.5,
      py: 1.25,
      borderRadius: 1.5,
      border: "1px solid",
      borderColor: "divider",
      bgcolor: "background.paper",
      display: "flex",
      alignItems: "center",
      gap: 1.25,
    }}
  >
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "var(--ec-status-neutral-bg)",
        flexShrink: 0,
      }}
    >
      <Icon sx={{ fontSize: 16, color: "text.secondary" }} />
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{
          color: "text.disabled",
          display: "block",
          fontSize: "0.68rem",
          lineHeight: 1,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          mt: 0.2,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value || "—"}
      </Typography>
    </Box>
  </Box>
);

const ChipList = ({ items, limit = 10 }) => {
  if (!items?.length) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }

  const normalised = items.map((item) => safeStr(item)).filter(Boolean);
  const visible = normalised.slice(0, limit);
  const overflow = normalised.length - visible.length;

  return (
    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
      {visible.map((item, idx) => (
        <Chip
          key={`${item}-${idx}`}
          label={item}
          size="small"
          variant="outlined"
          sx={{
            height: 22,
            borderRadius: 1,
            fontSize: "0.72rem",
            fontWeight: 500,
            borderColor: "divider",
            color: "text.secondary",
            "& .MuiChip-label": { px: "8px" },
          }}
        />
      ))}
      {overflow > 0 && (
        <Chip
          size="small"
          variant="outlined"
          label={`+${overflow} more`}
          sx={{
            height: 22,
            borderRadius: 1,
            fontSize: "0.72rem",
            fontWeight: 500,
            borderColor: "divider",
            color: "text.disabled",
            "& .MuiChip-label": { px: "8px" },
          }}
        />
      )}
    </Stack>
  );
};

const LoadingSkeleton = () => (
  <Grid container spacing={3} sx={{ p: 3 }}>
    <Grid item xs={12} lg={4}>
      <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
      <Stack spacing={0.75} sx={{ mt: 2 }}>
        {[100, 80, 100, 80, 60].map((w, i) => (
          <Skeleton key={i} variant="text" width={`${w}%`} height={24} />
        ))}
      </Stack>
    </Grid>

    <Grid item xs={12} lg={8}>
      <Skeleton
        variant="rectangular"
        height={48}
        sx={{ borderRadius: 1, mb: 2 }}
      />
      <Stack spacing={1.25}>
        {[100, 75, 90, 55, 80, 65, 70, 50].map((w, i) => (
          <Skeleton key={i} variant="text" width={`${w}%`} height={20} />
        ))}
      </Stack>
    </Grid>
  </Grid>
);

function ProductPopup({
  isOpen,
  onRequestClose,
  productId,
  instanceUniqueId,
  lookupType,
  product: initialProduct,
  readOnly = false,
}) {
  const theme = useTheme();
  const { authState } = useContext(AuthContext);
  const isAdmin = !readOnly && authState?.role === "administrator";

  const [isFullscreen, setIsFullscreen] = useState(false);

  const [product, setProduct] = useState(initialProduct || null);
  const hydratedProductIdRef = useRef(null);
  const initialProductId = initialProduct?.Id ?? initialProduct?.id ?? null;
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [activeTab, setActiveTab] = useState(TABS.OVERVIEW);
  const [activeAdminTab, setActiveAdminTab] = useState("siteImport");
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const [failedImages, setFailedImages] = useState([]);
  const [imageHovered, setImageHovered] = useState(false);
  const [showRawJson, setShowRawJson] = useState(false);


  const [syncing, setSyncing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  const [selectedSite, setSelectedSite] = useState("");
  const [selectedVariation, setSelectedVariation] = useState("");
  const [markupPrice, setMarkupPrice] = useState(0);
  const [percentageIncrease, setPercentageIncrease] = useState(40);
  const [baseIncrease, setBaseIncrease] = useState(0);

  const [selectedColor, setSelectedColor] = useState("");
  const [selectedSize, setSelectedSize] = useState("");
  const [category, setCategory] = useState("");
  const [brand, setBrand] = useState("");
  const [tags, setTags] = useState("");

  useEffect(() => {
    if (
      initialProductId != null &&
      String(hydratedProductIdRef.current) === String(initialProductId)
    ) {
      return;
    }
    setProduct(initialProduct || null);
  }, [initialProduct]);

  useEffect(() => {
    if (!isOpen) return;
    setActiveImageIdx(0);
    setFailedImages([]);
    setActiveTab(TABS.OVERVIEW);
    setShowRawJson(false);
    setLoadError("");
    setIsFullscreen(false);
  }, [isOpen, product?.Id, product?.id]);

  useEffect(() => {
    const detailProductId = resolveProductPopupDetailId({
      productId,
      lookupType,
      initialProduct: { Id: initialProductId },
    });
    if (!(isOpen && detailProductId)) return;

    let isMounted = true;

    const endpointMap = {
      "asi-test": `products/get-product/${detailProductId}`,
      "keyword-test": `products/get-product/${detailProductId}`,
      "asi-live": `products/get-asi-live-product/${detailProductId}`,
      "keyword-live": `products/get-asi-live-product/${detailProductId}`,
      custom: `products/retrieve-custom-product/${detailProductId}`,
      catalog: `catalog/products/${detailProductId}`,
      promoworld: `catalog/products/${detailProductId}`,
    };

    const endpoint =
      endpointMap[lookupType] || `products/get-product/${detailProductId}`;

    setLoading(true);
    setLoadError("");

    axios
      .get(buildProductPopupApiUrl(
        process.env.REACT_APP_NODEJS_SERVER_URL,
        endpoint,
      ))
      .then(({ data }) => {
        if (!isMounted) return;
        hydratedProductIdRef.current = detailProductId;
        setProduct(data || null);
      })
      .catch((err) => {
        if (!isMounted) return;
        console.error("[ProductPopup] Failed to fetch product:", err);
        setLoadError("Unable to load product details. Please try again.");
      })
      .finally(() => {
        if (!isMounted) return;
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isOpen, productId, lookupType, initialProductId]);

  const currencyCode = detectCurrencyCode(product);

  const images = useMemo(
    () => getValidProductImages(product, lookupType).filter(url => !failedImages.includes(url)),
    [product, lookupType, failedImages],
  );

  const handleImageError = (url) => {
    setFailedImages(previous => previous.includes(url) ? previous : [...previous, url]);
    setActiveImageIdx(0);
  };

  const colors = useMemo(() => getAvailableColors(product), [product]);
  const sizes = useMemo(() => getAvailableSizes(product), [product]);

  const productName = firstNonEmpty(product?.Name, "Product Detail");
  const productDescription = firstNonEmpty(
    product?.ShortDescription,
    product?.Description,
  );
  const productNumber = firstNonEmpty(
    product?.Number,
    product?.SKU,
    product?.ProductCode,
  );
  const supplierName = firstNonEmpty(
    product?.Supplier?.Name,
    product?.Supplier,
  );
  const countryBadge = firstNonEmpty(
    product?.Country,
    product?.CountryOfOrigin,
    product?.CountryName,
    product?.Origin?.[0]?.Name,
  );

  const themes = normalizeList(product?.Themes);
  const origins = normalizeList(product?.Origin);
  const warnings = normalizeList(product?.Warnings);
  const certs = normalizeList(product?.Certifications);
  const artworkTemplates = normalizeList(product?.ArtworkTemplates);
  const imprintServices = normalizeList(product?.Imprinting?.Services?.Values);
  const packagingValues = normalizeList(product?.Packaging?.Values);
  const surchargeItems = normalizeList(product?.CreditCardSurchargeFees);

  const imprintOptionGroups = normalizeList(
    product?.Imprinting?.OptionGroups ||
      product?.Imprinting?.Options?.Groups ||
      product?.Imprinting?.Groups,
  );

  const priceRows = useMemo(
    () => buildProductPopupPriceRows(product),
    [product],
  );

  const basePrice =
    priceRows[0]?.price ??
    product?.LowestPrice?.Price ??
    product?.HighestPrice?.Price ??
    null;
  const basePriceIsReference = priceRows[0]?.isReferencePrice === true;
  const hasReferencePrices = priceRows.some(
    (row) => row.isReferencePrice,
  );

  const hasInventory = Boolean(product?.HasInventory);

  const additionalImprintNotices = useMemo(() => {
    const notices = new Set();
    const methods = product?.Imprinting?.Methods?.Values || [];

    methods.forEach((method) => {
      normalizeList(method?.Charges).forEach((charge) => {
        const name = firstNonEmpty(
          charge?.Name,
          charge?.Description,
          charge?.TypeCode,
        );
        if (name) notices.add(`Additional imprint charge may apply: ${name}`);
      });
    });

    normalizeList(product?.AdditionalInfo).forEach((item) => {
      const text = firstNonEmpty(item?.Name, item?.Description, item);
      if (
        /imprint|setup|run charge|screen charge|additional charge/i.test(text)
      ) {
        notices.add(text);
      }
    });

    return Array.from(notices);
  }, [product]);

  const siteOptions = [
    {
      value: "https://elitepromomarketing.com",
      label: "Elite Promo Marketing",
    },
  ];

  const variationOptions = product
    ? [
        {
          id: product?.Id || product?.id,
          name: `${product?.Name || "Product"} (Original)`,
        },
        ...(product?.Variants || []).map((v) => ({
          id: v?.Id,
          name: v?.Name || `Variant ${v?.Id}`,
        })),
      ]
    : [];

  const handleCopy = useCallback((text) => {
    if (!text) return;
    navigator.clipboard
      .writeText(text)
      .then(() => setSnackbar({ open: true, message: "Copied to clipboard" }))
      .catch(() =>
        setSnackbar({
          open: true,
          message: "Copy failed — please select manually",
        }),
      );
  }, []);

  const handlePrevImage = useCallback(() => {
    setActiveImageIdx((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  const handleNextImage = useCallback(() => {
    setActiveImageIdx((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images.length]);

  const safeClose = useCallback(
    (...args) => {
      if (typeof onRequestClose === "function") onRequestClose(...args);
    },
    [onRequestClose],
  );

  const handleSyncProduct = useCallback(async () => {
    if (!product || syncing) return;

    const isCustomProduct = activeAdminTab === "customize";
    const productAttachedId = isCustomProduct ? instanceUniqueId : ["global"];

    try {
      setSyncing(true);

      await axios.post(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}products/create-product`,
        {
          InstanceUniqueIds: productAttachedId,
          AsiId: product.Id,
          IsCustomProduct: isCustomProduct,
          Name: product.Name,
          Description: product.Description,
          ShortDescription: product.ShortDescription,
          Number: product.Number,
          ImageUrl: product.ImageUrl,
          Images: product.Images,
          VirtualSampleImages: product.VirtualSampleImages,
          Supplier: product.Supplier,
          Categories: product.Categories,
          Themes: product.Themes,
          Origin: product.Origin,
          TradeNames: product.TradeNames,
          ProductionTime: product.ProductionTime,
          RushTime: product.RushTime,
          Prices: product.Prices,
          Attributes: product.Attributes,
          Imprinting: product.Imprinting,
          Options: product.Options,
          Shipping: product.Shipping,
          Variants: product.Variants,
          Warnings: product.Warnings,
          HasHazardMaterial: product.HasHazardMaterial,
          Prop65AdditionalInfo: product.Prop65AdditionalInfo,
          Currencies: product.Currencies,
          LowestPrice: product.LowestPrice,
          HighestPrice: product.HighestPrice,
          IsNew: product.IsNew,
          IsTrending: product.IsTrending,
          IsConfirmed: product.IsConfirmed,
          HasFullColorProcess: product.HasFullColorProcess,
          HasRushService: product.HasRushService,
          DistributorComments: product.DistributorComments,
          AdditionalInfo: product.AdditionalInfo,
          UpdateDate: product.UpdateDate,
          Sku: product.Sku,
          IsAssembled: product.IsAssembled,
          HasInventory: product.HasInventory,
          Services: product.Services,
        },
      );

      setSnackbar({ open: true, message: "Product synced successfully" });
      safeClose();
    } catch (err) {
      console.error("[ProductPopup] Sync failed:", err);
      setSnackbar({ open: true, message: "Sync failed — please try again" });
    } finally {
      setSyncing(false);
    }
  }, [product, syncing, activeAdminTab, instanceUniqueId, safeClose]);

  const handleDelete = useCallback(async () => {
    if (!product?.id || deleting) return;

    const confirmed = window.confirm(
      `Are you sure you want to deactivate "${product?.Name}"? This cannot be undone.`,
    );
    if (!confirmed) return;

    try {
      setDeleting(true);

      await axios.delete(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}products/delete-custom-product/${product.id}`,
        { headers: { accessToken: localStorage.getItem("accessToken") } },
      );

      safeClose(true);
    } catch (err) {
      console.error("[ProductPopup] Delete failed:", err);
      setSnackbar({
        open: true,
        message: "Could not deactivate this product — please try again",
      });
    } finally {
      setDeleting(false);
    }
  }, [product, deleting, safeClose]);

  const renderOverviewTab = () => (
    <Stack spacing={3}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
        <KpiTile
          icon={PriceChangeOutlinedIcon}
          label={basePriceIsReference ? "Reference Price" : "Starting Price"}
          value={formatMoney(basePrice, currencyCode)}
        />
        <KpiTile
          icon={PaletteOutlinedIcon}
          label="Colors"
          value={colors.length ? `${colors.length} available` : "—"}
        />
        <KpiTile
          icon={StraightenOutlinedIcon}
          label="Sizes"
          value={sizes.length ? `${sizes.length} available` : "—"}
        />
        <KpiTile
          icon={Inventory2OutlinedIcon}
          label="Stock"
          value={hasInventory ? "In Stock" : "Stock Varies"}
        />
      </Stack>

      <Box>
        <SectionHeader icon={InfoOutlinedIcon} label="Product Summary" />
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <FieldRow
              label="Description"
              value={productDescription || "—"}
              multiline
            />
            <FieldRow
              label="Supplier"
              value={
                supplierName ? (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <StorefrontOutlinedIcon
                      sx={{ fontSize: 13, color: "text.disabled" }}
                    />
                    <Typography variant="body2">{supplierName}</Typography>
                  </Stack>
                ) : (
                  "—"
                )
              }
            />
            <FieldRow
              label="Supplier Page"
              value={
                safeStr(product?.ProductUrl) ? (
                  <Link
                    href={product.ProductUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{
                      fontSize: "0.875rem",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.4,
                    }}
                  >
                    Open supplier page
                    <OpenInNewOutlinedIcon sx={{ fontSize: 12 }} />
                  </Link>
                ) : (
                  "—"
                )
              }
            />
            {product?.DistributorComments && (
              <FieldRow
                label="Notes"
                value={product.DistributorComments}
                multiline
              />
            )}
          </Grid>

          <Grid item xs={12} md={5}>
            <FieldRow
              label="Part / No."
              value={productNumber || "—"}
              mono
              onCopy={productNumber ? handleCopy : undefined}
            />
            <FieldRow
              label="ASI ID"
              value={product?.Id || "—"}
              mono
              onCopy={product?.Id ? handleCopy : undefined}
            />
            {lookupType === "custom" && (
              <FieldRow
                label="Core ID"
                value={product?.id || "—"}
                mono
                onCopy={product?.id ? handleCopy : undefined}
              />
            )}
            <FieldRow
              label="Country / Origin"
              value={
                countryBadge ||
                origins.map((o) => o?.Name || o).join(", ") ||
                "—"
              }
            />
            <FieldRow label="Currency" value={currencyCode} />
          </Grid>
        </Grid>
      </Box>

      <Box>
        <SectionHeader icon={CategoryOutlinedIcon} label="Attributes" />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 1.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                Colors
              </Typography>
              <ChipList items={colors} limit={12} />
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                Sizes
              </Typography>
              <ChipList items={sizes} limit={12} />
            </Box>
            {Array.isArray(product?.Variants) && product.Variants.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  Variations / Parts
                </Typography>
                <ChipList
                  items={product.Variants.map((variant) => {
                    const sku = resolveVariantSku(variant, product);
                    const label = composeVariantLabel(variant, product);
                    return label && label !== "Variant" ? `${sku} — ${label}` : sku;
                  }).filter(Boolean)}
                  limit={20}
                />
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 1.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                Themes
              </Typography>
              <ChipList items={themes} limit={10} />
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                Origin
              </Typography>
              <ChipList
                items={origins
                  .map((o) => (typeof o === "string" ? o : o?.Name))
                  .filter(Boolean)}
                limit={6}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box>
        <SectionHeader icon={AllInboxOutlinedIcon} label="Packaging" />
        <FieldRow
          label="Items per Package"
          value={product?.Shipping?.ItemsPerPackage || "—"}
        />
        <FieldRow
          label="Packaged Weight"
          value={product?.Shipping?.WeightPerPackage || "—"}
        />
        {packagingValues.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 0.5 }}
            >
              Packaging Types
            </Typography>
            <ChipList
              items={packagingValues
                .map((item) =>
                  firstNonEmpty(item?.Name, item?.Description, item),
                )
                .filter(Boolean)}
              limit={8}
            />
          </Box>
        )}
      </Box>
    </Stack>
  );

  const renderPricingTab = () => (
    <Stack spacing={3}>
      {surchargeItems.length > 0 && (
        <Alert
          severity="info"
          icon={<PaymentsOutlinedIcon fontSize="inherit" />}
          sx={{ borderRadius: 1.5, fontSize: "0.8125rem" }}
        >
          {surchargeItems
            .map((fee) =>
              firstNonEmpty(fee?.Name, fee?.Description, fee?.Value, fee),
            )
            .filter(Boolean)
            .join("  ·  ")}
        </Alert>
      )}

      {(product?.Options?.minimumOrder || product?.Options?.orderIncrement) && (
        <Box>
          <SectionHeader icon={TagOutlinedIcon} label="Order Rules" />
          {product?.Options?.minimumOrder && (
            <FieldRow
              label="Minimum Order"
              value={`${product.Options.minimumOrder} pcs`}
            />
          )}
          {product?.Options?.orderIncrement && (
            <FieldRow
              label="Order Increment"
              value={`Multiples of ${product.Options.orderIncrement}`}
            />
          )}
        </Box>
      )}

      <Box>
        <SectionHeader icon={PriceChangeOutlinedIcon} label="Price Matrix" />
        {priceRows.length ? (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ borderRadius: 1.5, boxShadow: "none" }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Variant", "SKU", "Key", "Quantity", "Unit Price"].map(
                    (h) => (
                      <TableCell
                        key={h}
                        sx={{
                          bgcolor: alpha(theme.palette.background.default, 1),
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          color: "text.secondary",
                          borderBottom: "2px solid",
                          borderColor: "divider",
                          py: 1.25,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {h}
                      </TableCell>
                    ),
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {priceRows.map((row) => (
                  <TableRow
                    key={row.key}
                    hover
                    sx={{ "&:last-child td": { border: 0 } }}
                  >
                    <TableCell sx={{ fontSize: "0.8125rem" }}>
                      {row.variant}
                    </TableCell>
                    <TableCell
                      sx={{ fontFamily: "monospace", fontSize: "0.8125rem" }}
                    >
                      {row.sku || "—"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.8125rem" }}>
                      {row.label || "—"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.8125rem" }}>
                      {getProductPopupPricePresentation(row).quantity}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.8125rem",
                        color: "primary.main",
                      }}
                    >
                      <Stack spacing={0} alignItems="flex-end">
                        <span>{formatMoney(row.price, currencyCode)}</span>
                        {row.isReferencePrice && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 500 }}
                          >
                            {getProductPopupPricePresentation(row).amountLabel}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="info" sx={{ borderRadius: 1.5 }}>
            No pricing information available for this product.
          </Alert>
        )}
        {hasReferencePrices && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}
          >
            Confirm supplier quantity breaks and decoration charges.
          </Typography>
        )}
      </Box>
    </Stack>
  );

  const renderImprintTab = () => {
    const imprintMethods = product?.Imprinting?.Methods?.Values || [];

    return (
      <Stack spacing={3}>
        <Box>
          <SectionHeader icon={StyleOutlinedIcon} label="Imprint Overview" />
          <FieldRow
            label="Sold Unimprinted"
            value={product?.Imprinting?.SoldUnimprinted ? "Yes" : "No"}
          />

          {product?.Imprinting?.Colors?.Values?.length > 0 && (
            <Box sx={{ mt: 1.25 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                Available Imprint Colors
              </Typography>
              <ChipList
                items={product.Imprinting.Colors.Values.map(
                  (c) => c?.Name,
                ).filter(Boolean)}
                limit={14}
              />
            </Box>
          )}

          {product?.Imprinting?.Sizes?.Values?.length > 0 && (
            <Box sx={{ mt: 1.25 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                Available Imprint Sizes
              </Typography>
              <ChipList
                items={product.Imprinting.Sizes.Values.map(
                  (s) => s?.Name,
                ).filter(Boolean)}
                limit={12}
              />
            </Box>
          )}

          {product?.Imprinting?.Locations?.Values?.length > 0 && (
            <Box sx={{ mt: 1.25 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                Imprint Locations
              </Typography>
              <ChipList
                items={(product.Imprinting.Locations.Values || [])
                  .map((l) => (typeof l === "string" ? l : l?.Name))
                  .filter(Boolean)}
                limit={12}
              />
            </Box>
          )}
        </Box>

        {imprintServices.length > 0 && (
          <Box>
            <SectionHeader
              icon={MiscellaneousServicesOutlinedIcon}
              label="Imprint Services"
            />
            <ChipList
              items={imprintServices
                .map((item) =>
                  firstNonEmpty(item?.Name, item?.Description, item),
                )
                .filter(Boolean)}
              limit={12}
            />
          </Box>
        )}

        {artworkTemplates.length > 0 && (
          <Box>
            <SectionHeader
              icon={ArticleOutlinedIcon}
              label="Artwork Templates"
            />
            <Stack spacing={0.75}>
              {artworkTemplates.map((tpl, idx) => {
                const label = firstNonEmpty(
                  tpl?.Name,
                  tpl?.Description,
                  `Template ${idx + 1}`,
                );
                const url = firstNonEmpty(
                  tpl?.Url,
                  tpl?.URL,
                  tpl?.Link,
                  tpl?.Href,
                );
                return (
                  <Box key={idx}>
                    {url ? (
                      <Link
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        sx={{
                          fontSize: "0.875rem",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.4,
                        }}
                      >
                        {label}
                        <OpenInNewOutlinedIcon sx={{ fontSize: 12 }} />
                      </Link>
                    ) : (
                      <Typography variant="body2">{label}</Typography>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Box>
        )}

        {imprintOptionGroups.length > 0 && (
          <Box>
            <SectionHeader
              icon={ViewAgendaOutlinedIcon}
              label="Imprint Option Groups"
            />
            <Stack spacing={1.5}>
              {imprintOptionGroups.map((group, idx) => (
                <Box key={idx}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {firstNonEmpty(
                      group?.Name,
                      group?.Label,
                      `Group ${idx + 1}`,
                    )}
                  </Typography>
                  <ChipList
                    items={normalizeList(group?.Values || group?.Options)
                      .map((item) =>
                        firstNonEmpty(item?.Name, item?.Description, item),
                      )
                      .filter(Boolean)}
                    limit={10}
                  />
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {additionalImprintNotices.length > 0 && (
          <Alert
            severity="warning"
            icon={<ReportProblemOutlinedIcon fontSize="inherit" />}
            sx={{ borderRadius: 1.5 }}
          >
            <Stack spacing={0.5}>
              {additionalImprintNotices.map((notice, idx) => (
                <Typography key={idx} variant="body2">
                  {notice}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}

        {imprintMethods.length > 0 && (
          <Box>
            <SectionHeader icon={LayersOutlinedIcon} label="Imprint Methods" />
            <Stack spacing={1}>
              {imprintMethods.map((method, idx) => (
                <Accordion
                  key={idx}
                  disableGutters
                  elevation={0}
                  defaultExpanded={idx === 0}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: "8px !important",
                    "&:before": { display: "none" },
                    "&.Mui-expanded": { borderColor: "primary.main" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
                    sx={{
                      minHeight: 44,
                      bgcolor: alpha(theme.palette.background.default, 0.6),
                      borderRadius: "8px",
                      "& .MuiAccordionSummary-content": { my: 0.75 },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {method?.Name || `Method ${idx + 1}`}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            {[
                              "Quantity",
                              "Base Price",
                              "Total (incl. charges)",
                            ].map((h) => (
                              <TableCell
                                key={h}
                                sx={{
                                  bgcolor: alpha(
                                    theme.palette.background.default,
                                    1,
                                  ),
                                  fontWeight: 700,
                                  fontSize: "0.72rem",
                                  letterSpacing: "0.06em",
                                  textTransform: "uppercase",
                                  color: "text.secondary",
                                  py: 1,
                                }}
                              >
                                {h}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(product?.Prices || []).map((price, pIdx) => {
                            const charges = calculateImprintCharges(
                              method?.Charges || [],
                              price?.Quantity?.From || 1,
                              method?.Code,
                            );
                            return (
                              <TableRow
                                key={pIdx}
                                hover
                                sx={{ "&:last-child td": { border: 0 } }}
                              >
                                <TableCell sx={{ fontSize: "0.8125rem" }}>
                                  {formatQtyRange(
                                    price?.Quantity?.From,
                                    price?.Quantity?.To,
                                  )}
                                </TableCell>
                                <TableCell sx={{ fontSize: "0.8125rem" }}>
                                  {formatMoney(price?.Price, currencyCode)}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: "0.8125rem",
                                    color: "primary.main",
                                  }}
                                >
                                  {formatMoney(
                                    Number(price?.Price || 0) + charges,
                                    currencyCode,
                                  )}
                                  {charges > 0 && (
                                    <Typography
                                      component="span"
                                      variant="caption"
                                      sx={{ color: "text.disabled", ml: 0.5 }}
                                    >
                                      (+{formatMoney(charges, currencyCode)})
                                    </Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          </Box>
        )}

        {imprintMethods.length === 0 && (
          <Alert severity="info" sx={{ borderRadius: 1.5 }}>
            No imprint method details available for this product.
          </Alert>
        )}
      </Stack>
    );
  };

  const renderShippingTab = () => {
    const shippingOptions = product?.Shipping?.Options?.Values || [];
    const skuRows = product?.SKU || product?.Sku || [];

    return (
      <Stack spacing={3}>
        <Box>
          <SectionHeader icon={LocalShippingOutlinedIcon} label="Lead Times" />
          <FieldRow
            label="Production Time"
            value={
              product?.ProductionTime?.[0]?.Name ||
              (product?.ProductionTime?.[0]?.Days
                ? `${product.ProductionTime[0].Days} days`
                : "—")
            }
          />
          <FieldRow
            label="Rush Time"
            value={
              product?.RushTime?.[0]?.Name ||
              (product?.RushTime?.[0]?.Days
                ? `${product.RushTime[0].Days} days`
                : "—")
            }
          />
          <FieldRow
            label="Supplier Site"
            value={
              safeStr(product?.Supplier?.Websites?.[0]) ? (
                <Link
                  href={product.Supplier.Websites[0]}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={{
                    fontSize: "0.875rem",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.4,
                  }}
                >
                  {(() => {
                    try {
                      return new URL(product.Supplier.Websites[0]).hostname;
                    } catch {
                      return "Visit website";
                    }
                  })()}
                  <OpenInNewOutlinedIcon sx={{ fontSize: 12 }} />
                </Link>
              ) : (
                "—"
              )
            }
          />
        </Box>

        <Box>
          <SectionHeader
            icon={AllInboxOutlinedIcon}
            label="Package & Dimensions"
          />
          <FieldRow
            label="Packaged Weight"
            value={
              product?.Shipping?.Weight?.Values?.[0]?.Name ||
              product?.Shipping?.WeightPerPackage ||
              "—"
            }
          />
          <FieldRow
            label="Dimensions"
            value={product?.Shipping?.Dimensions?.Description || "—"}
          />
          <FieldRow
            label="Items / Package"
            value={product?.Shipping?.ItemsPerPackage || "—"}
          />
        </Box>

        {shippingOptions.length > 0 && (
          <Box>
            <SectionHeader
              icon={LocalShippingOutlinedIcon}
              label="Shipping Options"
            />
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: 1.5, boxShadow: "none" }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["Service", "Price", "Cost", "Quantity"].map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          bgcolor: alpha(theme.palette.background.default, 1),
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          color: "text.secondary",
                          borderBottom: "2px solid",
                          borderColor: "divider",
                          py: 1.25,
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shippingOptions.map((opt, idx) => {
                    const serviceName =
                      typeof opt === "string"
                        ? opt
                        : opt?.Name || opt?.Type || "—";
                    const priceObj =
                      typeof opt === "object" ? opt?.Prices?.[0] : null;

                    return (
                      <TableRow
                        key={idx}
                        hover
                        sx={{ "&:last-child td": { border: 0 } }}
                      >
                        <TableCell sx={{ fontSize: "0.8125rem" }}>
                          {serviceName}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.8125rem" }}>
                          {formatMoney(priceObj?.Price, currencyCode)}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.8125rem" }}>
                          {formatMoney(priceObj?.Cost, currencyCode)}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.8125rem" }}>
                          {priceObj
                            ? formatQtyRange(
                                priceObj?.Quantity?.From,
                                priceObj?.Quantity?.To,
                              )
                            : "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {skuRows.length > 0 && (
          <Box>
            <SectionHeader icon={TagOutlinedIcon} label="SKU Matrix" />
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: 1.5, boxShadow: "none" }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["SKU", "Size", "Color"].map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          bgcolor: alpha(theme.palette.background.default, 1),
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          color: "text.secondary",
                          borderBottom: "2px solid",
                          borderColor: "divider",
                          py: 1.25,
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {skuRows.map((s, idx) => {
                    const vals = s?.Values || [];
                    const size = vals.find((v) => v?.Code === "SIZE")?.Name;
                    const color = vals.find((v) => v?.Code === "PRCL")?.Name;
                    return (
                      <TableRow
                        key={idx}
                        hover
                        sx={{ "&:last-child td": { border: 0 } }}
                      >
                        <TableCell
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "0.8125rem",
                          }}
                        >
                          {s?.SKU || "—"}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.8125rem" }}>
                          {size || "—"}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.8125rem" }}>
                          {color || "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Stack>
    );
  };

  const renderSafetyTab = () => (
    <Stack spacing={3}>
      {warnings.length > 0 && (
        <Box>
          <SectionHeader icon={ReportProblemOutlinedIcon} label="Warnings" />
          <Stack spacing={1}>
            {warnings.map((w, idx) => (
              <Alert
                key={idx}
                severity="warning"
                icon={<ReportProblemOutlinedIcon fontSize="inherit" />}
                sx={{ borderRadius: 1.5, fontSize: "0.8125rem" }}
              >
                {w?.Warning || w?.Description || safeStr(w)}
              </Alert>
            ))}
          </Stack>
        </Box>
      )}

      <Box>
        <SectionHeader icon={SafetyCheckOutlinedIcon} label="Compliance" />
        <FieldRow
          label="Prop 65"
          value={product?.Prop65AdditionalInfo || "—"}
        />
        <FieldRow label="Disclaimer" value={product?.Disclaimer || "—"} />

        {certs.length > 0 && (
          <Box sx={{ mt: 1.25 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 0.5 }}
            >
              Certifications
            </Typography>
            <Stack spacing={0.75}>
              {certs.map((cert, idx) => {
                const certName = cert?.Name || safeStr(cert) || "—";
                const isUrl = /^https?:/i.test(certName);
                return (
                  <Box
                    key={idx}
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    <VerifiedOutlinedIcon
                      sx={{ fontSize: 14, color: "success.main" }}
                    />
                    <Typography variant="body2">
                      {cert?.Code ? <strong>{cert.Code}: </strong> : null}
                      {isUrl ? (
                        <Link
                          href={certName}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                        >
                          Download specification sheet
                        </Link>
                      ) : (
                        certName
                      )}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        )}
      </Box>

      {!warnings.length &&
        !product?.Prop65AdditionalInfo &&
        !certs.length &&
        !product?.Disclaimer && (
          <Alert severity="info" sx={{ borderRadius: 1.5 }}>
            No safety or compliance information available for this product.
          </Alert>
        )}
    </Stack>
  );

  const renderAdminTools = () => {
    if (!isAdmin || lookupType === "custom") return null;

    return (
      <Box sx={{ mb: 2.5 }}>
        <Accordion
          disableGutters
          elevation={0}
          defaultExpanded={false}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "10px !important",
            "&:before": { display: "none" },
            "&.Mui-expanded": { borderColor: "primary.main" },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
            sx={{
              minHeight: 46,
              bgcolor: alpha(theme.palette.background.default, 0.7),
              borderRadius: "10px",
              "& .MuiAccordionSummary-content": { my: 0.75 },
            }}
          >
            {/* <Stack direction="row" spacing={1} alignItems="center">
              <TuneOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Admin Tools
              </Typography>
            </Stack> */}

            <Stack spacing={0.75} sx={{ py: 0.25 }}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  minWidth: 0,
                  flexWrap: "nowrap",
                  overflow: "hidden",
                }}
              >
                <TuneOutlinedIcon
                  sx={{ fontSize: 16, color: "text.secondary", flexShrink: 0 }}
                />

                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, flexShrink: 0 }}
                >
                  Admin Tools
                </Typography>

                <Chip
                  size="small"
                  label="3 actions"
                  variant="outlined"
                  sx={{
                    height: 20,
                    flexShrink: 0,
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    borderColor: "divider",
                    color: "text.secondary",
                    "& .MuiChip-label": { px: 0.9 },
                  }}
                />

                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    minWidth: 0,
                  }}
                >
                  Import to Site • Customize • Discussion
                </Typography>
              </Stack>
            </Stack>
          </AccordionSummary>

          <AccordionDetails
            sx={{ p: 0, borderTop: "1px solid", borderColor: "divider" }}
          >
            <Box sx={{ p: 2 }}>
              <Tabs
                value={activeAdminTab}
                onChange={(_, v) => setActiveAdminTab(v)}
                variant="scrollable"
                allowScrollButtonsMobile
                sx={{
                  minHeight: 40,
                  mb: 1.5,
                  "& .MuiTab-root": { minHeight: 40, fontSize: "0.8125rem" },
                }}
              >
                <Tab
                  value="siteImport"
                  icon={<CloudUploadOutlinedIcon fontSize="small" />}
                  iconPosition="start"
                  label="Import to Site"
                />
                <Tab
                  value="customize"
                  icon={<AutoAwesomeMotionOutlinedIcon fontSize="small" />}
                  iconPosition="start"
                  label="Customize"
                />
                <Tab
                  value="productChat"
                  icon={<ForumOutlinedIcon fontSize="small" />}
                  iconPosition="start"
                  label="Discussion"
                />
              </Tabs>

              {activeAdminTab === "siteImport" && (
<>
                  <Typography
      variant="body2"
      color="text.secondary"
      sx={{
        mb: 2,
        lineHeight: 1.6,
      }}
    >
      Select a website and pick either the original product or one of its
      ready-made variants—then click “Import.” We’ll automatically sync
      images, descriptions, pricing, SKUs and imprint settings so you can
      start selling instantly.
    </Typography>

                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      label="Target Site"
                      size="small"
                      fullWidth
                      value={selectedSite}
                      onChange={(e) => setSelectedSite(e.target.value)}
                    >
                      <MenuItem value="">Select a site</MenuItem>
                      {siteOptions.map((s) => (
                        <MenuItem key={s.value} value={s.value}>
                          {s.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <TextField
                      select
                      label="Variation"
                      size="small"
                      fullWidth
                      value={selectedVariation}
                      onChange={(e) => setSelectedVariation(e.target.value)}
                    >
                      <MenuItem value="">Select a variation</MenuItem>
                      {variationOptions.map((v) => (
                        <MenuItem key={v.id} value={v.id}>
                          {v.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Button
                      fullWidth
                      variant="contained"
                      disabled={!selectedSite || !selectedVariation}
                      onClick={() =>
                        window.alert(
                          `Importing ASI product ${product?.Id} as ${selectedVariation} to ${selectedSite}`,
                        )
                      }
                      sx={{ height: 40 }}
                    >
                      Import
                    </Button>
                  </Grid>
                </Grid>
                </>
              )}

              {/* {activeAdminTab === "customize" && (
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="Markup Price"
                      size="small"
                      type="number"
                      fullWidth
                      value={markupPrice}
                      onChange={(e) => setMarkupPrice(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="Percentage Increase (%)"
                      size="small"
                      type="number"
                      fullWidth
                      value={percentageIncrease}
                      onChange={(e) => setPercentageIncrease(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="Base Increase"
                      size="small"
                      type="number"
                      fullWidth
                      value={baseIncrease}
                      onChange={(e) => setBaseIncrease(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<SyncOutlinedIcon />}
                      onClick={handleSyncProduct}
                      disabled={syncing}
                      sx={{ height: 40 }}
                    >
                      {syncing ? "Syncing…" : "Sync"}
                    </Button>
                  </Grid>
                </Grid>
              )} */}
              {activeAdminTab === "customize" && (
                <Stack spacing={2.5}>
                  <Box
                    sx={{
                      p: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1.5,
                      bgcolor: "background.paper",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, mb: 0.5 }}
                    >
                      Quick create
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1.5 }}
                    >
                      One-click clone: sync a ready-to-sell version using the
                      current default product data.
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={handleSyncProduct}
                      disabled={syncing}
                      startIcon={
                        syncing ? (
                          <CircularProgress size={16} />
                        ) : (
                          <SyncOutlinedIcon />
                        )
                      }
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: 1,
                      }}
                    >
                      {syncing ? "Syncing..." : "Sync Product"}
                    </Button>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1.5,
                      bgcolor: "background.paper",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, mb: 0.5 }}
                    >
                      Customize Product
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      Update the UI fields below using the previous version
                      layout, without changing current sync behavior.
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="Markup Price"
                          type="number"
                          fullWidth
                          value={markupPrice}
                          onChange={(e) => setMarkupPrice(e.target.value)}
                          size="small"
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField
                          label="Percentage Increase (%)"
                          type="number"
                          fullWidth
                          value={percentageIncrease}
                          onChange={(e) =>
                            setPercentageIncrease(e.target.value)
                          }
                          size="small"
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField
                          label="Base Increase (CAD)"
                          type="number"
                          fullWidth
                          value={baseIncrease}
                          onChange={(e) => setBaseIncrease(e.target.value)}
                          size="small"
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Color"
                          fullWidth
                          value={selectedColor}
                          onChange={(e) => setSelectedColor(e.target.value)}
                          size="small"
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Size"
                          fullWidth
                          value={selectedSize}
                          onChange={(e) => setSelectedSize(e.target.value)}
                          size="small"
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Category"
                          fullWidth
                          value={category}
                          onChange={(e) => setCategory(e.target.value)}
                          size="small"
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Brand"
                          fullWidth
                          value={brand}
                          onChange={(e) => setBrand(e.target.value)}
                          size="small"
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label="Tags (comma-separated)"
                          fullWidth
                          value={tags}
                          onChange={(e) => setTags(e.target.value)}
                          size="small"
                        />
                      </Grid>
                    </Grid>

                    <Stack direction="row" spacing={1.25} sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        onClick={handleSyncProduct}
                        disabled={syncing}
                        startIcon={
                          syncing ? (
                            <CircularProgress size={16} />
                          ) : (
                            <SyncOutlinedIcon />
                          )
                        }
                        sx={{
                          textTransform: "none",
                          fontWeight: 700,
                          borderRadius: 1,
                        }}
                      >
                        {syncing ? "Creating..." : "Create Product"}
                      </Button>
                    </Stack>
                  </Box>
                </Stack>
              )}

              {activeAdminTab === "productChat" && (
                <Alert severity="info" sx={{ borderRadius: 1.5 }}>
                  Team discussion for this product will be available in a future
                  release.
                </Alert>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  };

  const renderContent = () => {
    if (loading) return <LoadingSkeleton />;

    if (loadError) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ borderRadius: 1.5 }}>
            {loadError}
          </Alert>
        </Box>
      );
    }

    if (!product) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="info" sx={{ borderRadius: 1.5 }}>
            No product details available.
          </Alert>
        </Box>
      );
    }

    const activeImage = images[activeImageIdx] || images[0] || null;

    return (
      <Box sx={{ p: { xs: 2, md: 2.5 } }}>
        {renderAdminTools()}

        <Grid container spacing={3}>
          <Grid item xs={12} lg={4}>
            <Stack spacing={2}>
              <Paper
                variant="outlined"
                sx={{ borderRadius: 2, overflow: "hidden", boxShadow: "none" }}
              >
                <Box
                  position="relative"
                  sx={{
                    aspectRatio: "4 / 3",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: alpha(theme.palette.action.hover, 0.3),
                  }}
                  onMouseEnter={() => setImageHovered(true)}
                  onMouseLeave={() => setImageHovered(false)}
                >
                  {activeImage ? (
                    <Box
                      component="img"
                      key={activeImage}
                      src={activeImage}
                      onError={() => handleImageError(activeImage)}
                      alt={productName}
                      sx={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        boxSizing: "border-box",
                        objectFit: "contain",
                        p: 2,
                        transition: "opacity 0.15s ease",
                      }}
                    />
                  ) : (
                    <Stack
                      alignItems="center"
                      spacing={1}
                      color="text.disabled"
                    >
                      <ImageNotSupportedOutlinedIcon
                        sx={{ fontSize: 40, opacity: 0.3 }}
                      />
                      <Typography variant="caption">
                        No image available
                      </Typography>
                    </Stack>
                  )}

                  {activeImage && imageHovered && (
                    <Tooltip title="Open image in new tab" placement="top">
                      <IconButton
                        size="small"
                        component="a"
                        href={activeImage}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          position: "absolute",
                          top: 10,
                          right: 10,
                          bgcolor: alpha(theme.palette.common.black, 0.5),
                          color: "common.white",
                          "&:hover": {
                            bgcolor: alpha(theme.palette.common.black, 0.7),
                          },
                          transition: "opacity 0.15s ease",
                        }}
                      >
                        <OpenInNewOutlinedIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  )}

                  {images.length > 1 && (
                    <>
                      <IconButton
                        size="small"
                        aria-label="Previous image"
                        onClick={handlePrevImage}
                        sx={{
                          position: "absolute",
                          left: 10,
                          top: "50%",
                          transform: "translateY(-50%)",
                          bgcolor: alpha(theme.palette.common.black, 0.45),
                          color: "common.white",
                          "&:hover": {
                            bgcolor: alpha(theme.palette.common.black, 0.65),
                          },
                        }}
                      >
                        <ChevronLeftRoundedIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label="Next image"
                        onClick={handleNextImage}
                        sx={{
                          position: "absolute",
                          right: 10,
                          top: "50%",
                          transform: "translateY(-50%)",
                          bgcolor: alpha(theme.palette.common.black, 0.45),
                          color: "common.white",
                          "&:hover": {
                            bgcolor: alpha(theme.palette.common.black, 0.65),
                          },
                        }}
                      >
                        <ChevronRightRoundedIcon />
                      </IconButton>
                    </>
                  )}

                  {images.length > 1 && (
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 10,
                        right: 10,
                        bgcolor: alpha(theme.palette.common.black, 0.5),
                        color: "common.white",
                        borderRadius: "20px",
                        px: 1,
                        py: 0.25,
                        fontSize: "0.72rem",
                        fontWeight: 600,
                        lineHeight: 1.5,
                        userSelect: "none",
                      }}
                    >
                      {activeImageIdx + 1} / {images.length}
                    </Box>
                  )}
                </Box>

                {images.length > 1 && (
                  <Box
                    sx={{
                      p: 1,
                      borderTop: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={0.75}
                      sx={{ overflowX: "auto", pb: 0.25 }}
                    >
                      {images.map((img, idx) => (
                        <Box
                          key={idx}
                          role="button"
                          tabIndex={0}
                          aria-label={`View image ${idx + 1}`}
                          onClick={() => setActiveImageIdx(idx)}
                          onKeyDown={(e) =>
                            e.key === "Enter" && setActiveImageIdx(idx)
                          }
                          sx={{
                            width: 68,
                            height: 68,
                            flexShrink: 0,
                            borderRadius: 1.25,
                            overflow: "hidden",
                            cursor: "pointer",
                            border: "2px solid",
                            borderColor:
                              idx === activeImageIdx
                                ? "primary.main"
                                : "divider",
                            bgcolor: "background.default",
                            transition: "border-color 0.15s ease",
                            "&:hover": { borderColor: "primary.light" },
                          }}
                        >
                          <Box
                            component="img"
                            src={img}
                            onError={() => handleImageError(img)}
                            alt={`Thumbnail ${idx + 1}`}
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                              p: 0.5,
                            }}
                          />
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Paper>

              <Box>
                <SectionHeader icon={InfoOutlinedIcon} label="Status" />
                <Stack spacing={0.75}>
                  <UIStatusChip
                    tone={hasInventory ? "success" : "warning"}
                    label={hasInventory ? "In Stock" : "Stock Varies"}
                    icon={<Inventory2OutlinedIcon />}
                    sx={{ justifyContent: "flex-start", width: "100%" }}
                  />

                  {currencyCode && (
                    <UIStatusChip
                      tone="info"
                      label={`${currencyCode} Pricing`}
                      icon={<PriceChangeOutlinedIcon />}
                      sx={{ justifyContent: "flex-start", width: "100%" }}
                    />
                  )}

                  {product?.HasRushService && (
                    <UIStatusChip
                      tone="accent"
                      label="Rush Service Available"
                      icon={<LocalShippingOutlinedIcon />}
                      sx={{ justifyContent: "flex-start", width: "100%" }}
                    />
                  )}

                  {product?.IsNew && (
                    <UIStatusChip
                      tone="accent"
                      label="New Product"
                      icon={<LabelOutlinedIcon />}
                      sx={{ justifyContent: "flex-start", width: "100%" }}
                    />
                  )}

                  {product?.IsTrending && (
                    <UIStatusChip
                      tone="accent"
                      label="Trending"
                      icon={<LabelOutlinedIcon />}
                      sx={{ justifyContent: "flex-start", width: "100%" }}
                    />
                  )}

                  {product?.HasFullColorProcess && (
                    <UIStatusChip
                      tone="neutral"
                      label="Full Colour Process"
                      icon={<PaletteOutlinedIcon />}
                      sx={{ justifyContent: "flex-start", width: "100%" }}
                    />
                  )}

                  {product?.IsConfirmed && (
                    <UIStatusChip
                      tone="success"
                      label="Confirmed"
                      icon={<VerifiedOutlinedIcon />}
                      sx={{ justifyContent: "flex-start", width: "100%" }}
                    />
                  )}
                </Stack>
              </Box>
            </Stack>
          </Grid>

          <Grid item xs={12} lg={8}>
            <Paper
              variant="outlined"
              sx={{ borderRadius: 2, overflow: "hidden", boxShadow: "none" }}
            >
              <Box
                sx={{
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  bgcolor: alpha(theme.palette.background.default, 0.5),
                  px: 0.5,
                }}
              >
                <Tabs
                  value={activeTab}
                  onChange={(_, v) => setActiveTab(v)}
                  variant="scrollable"
                  allowScrollButtonsMobile
                  sx={{
                    minHeight: 46,
                    "& .MuiTab-root": {
                      minHeight: 46,
                      fontSize: "0.8125rem",
                      fontWeight: 600,
                      textTransform: "none",
                      letterSpacing: 0,
                      px: 1.75,
                    },
                  }}
                >
                  <Tab value={TABS.OVERVIEW} label="Overview" />
                  <Tab value={TABS.PRICING} label="Pricing" />
                  <Tab value={TABS.IMPRINT} label="Imprint" />
                  <Tab value={TABS.SHIPPING} label="Shipping" />
                  <Tab value={TABS.SAFETY} label="Safety" />
                </Tabs>
              </Box>

              <Box
                sx={{
                  p: { xs: 2, md: 2.25 },
                  minHeight: 320,
                  overflowY: "auto",
                  maxHeight: "calc(100vh - 340px)",
                  "&::-webkit-scrollbar": { width: 5 },
                  "&::-webkit-scrollbar-thumb": {
                    bgcolor: alpha(theme.palette.text.disabled, 0.3),
                    borderRadius: 99,
                  },
                  "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
                }}
              >
                {activeTab === TABS.OVERVIEW && renderOverviewTab()}
                {activeTab === TABS.PRICING && renderPricingTab()}
                {activeTab === TABS.IMPRINT && renderImprintTab()}
                {activeTab === TABS.SHIPPING && renderShippingTab()}
                {activeTab === TABS.SAFETY && renderSafetyTab()}
              </Box>
            </Paper>

            <Box
              sx={{
                mt: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                sx={{ px: 2, py: 1.5 }}
              >
                <Box>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <DataObjectOutlinedIcon
                      sx={{ fontSize: 15, color: "text.disabled" }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      Raw Payload
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.disabled">
                    Collapse during normal operation — for debugging only.
                  </Typography>
                </Box>

                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setShowRawJson((v) => !v)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 1.5,
                  }}
                >
                  {showRawJson ? "Hide" : "Show"} product data
                </Button>
              </Stack>

              {showRawJson && (
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 2,
                    maxHeight: 380,
                    overflowY: "auto",
                    // bgcolor: alpha(theme.palette.action.hover, 0.5),
                    // borderTop: "1px solid",
                    // borderColor: "divider",
                    // fontSize: 11.5,
                    // fontFamily: "monospace",
                    // whiteSpace: "pre-wrap",
                    // wordBreak: "break-all",
                    // lineHeight: 1.6,
                    // color: "text.secondary",

                    bgcolor: "#111827",
borderTop: "1px solid",
borderColor: "#374151",



fontSize: 12,
fontFamily:
  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',

whiteSpace: "pre-wrap",
wordBreak: "break-word",

lineHeight: 1.65,

color: "#A7F3D0",
                    "&::-webkit-scrollbar": { width: 5 },
                    // "&::-webkit-scrollbar-thumb": {
                    //   bgcolor: alpha(theme.palette.text.disabled, 0.3),
                    //   borderRadius: 99,
                    // },
                    "&::-webkit-scrollbar-thumb": {
  bgcolor: "#4B5563",
  borderRadius: 99,
},
                  }}
                >
                  {JSON.stringify(product, null, 2)}
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderHeader = () => {
    if (loading && !product) {
      return (
        <Box sx={{ px: { xs: 2, md: 3 }, py: 1.75 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Skeleton variant="text" width={280} height={32} />
            <Skeleton variant="circular" width={32} height={32} />
          </Stack>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.75,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: alpha(theme.palette.background.paper, 0.97),
          flexShrink: 0,
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                lineHeight: 1.2,
                wordBreak: "break-word",
                mb: 0.5,
              }}
            >
              {productName}
            </Typography>

            {product && (
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  display: "block",
                  mb: 1,
                  lineHeight: 1.4,
                }}
              >
                {[
                  product?.Id ? `ASI: ${product.Id}` : null,
                  productNumber ? `Part: ${productNumber}` : null,
                  supplierName ? `Supplier: ${supplierName}` : null,
                  countryBadge ? countryBadge : null,
                ]
                  .filter(Boolean)
                  .join("  ·  ")}
              </Typography>
            )}

            {product && (
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                <UIStatusChip
                  tone={hasInventory ? "success" : "warning"}
                  label={hasInventory ? "In Stock" : "Stock Varies"}
                  icon={<Inventory2OutlinedIcon />}
                />

                {productNumber && (
                  <Tooltip title="Click to copy part number" placement="top">
                    <Chip
                      size="small"
                      variant="outlined"
                      icon={<TagOutlinedIcon />}
                      label={productNumber}
                      onClick={() => handleCopy(productNumber)}
                      sx={{
                        height: 24,
                        borderRadius: 1,
                        fontSize: "0.72rem",
                        fontWeight: 500,
                        cursor: "pointer",
                        borderColor: "divider",
                        color: "text.secondary",
                        fontFamily: "monospace",
                        "& .MuiChip-label": { px: "8px" },
                        "&:hover": {
                          borderColor: "primary.main",
                          color: "primary.main",
                        },
                        transition: "all 0.15s ease",
                      }}
                    />
                  </Tooltip>
                )}

                {product?.Id && (
                  <Tooltip title="Click to copy ASI ID" placement="top">
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`ASI: ${product.Id}`}
                      onClick={() => handleCopy(safeStr(product.Id))}
                      sx={{
                        height: 24,
                        borderRadius: 1,
                        fontSize: "0.72rem",
                        fontWeight: 500,
                        cursor: "pointer",
                        borderColor: "divider",
                        color: "text.secondary",
                        fontFamily: "monospace",
                        "& .MuiChip-label": { px: "8px" },
                        "&:hover": {
                          borderColor: "primary.main",
                          color: "primary.main",
                        },
                        transition: "all 0.15s ease",
                      }}
                    />
                  </Tooltip>
                )}

                {countryBadge && (
                  <UIStatusChip
                    tone="neutral"
                    label={countryBadge}
                    icon={<FlagOutlinedIcon />}
                  />
                )}

                {safeStr(product?.ProductUrl) && (
                  <Tooltip title="Open on supplier site" placement="top">
                    <Chip
                      clickable
                      size="small"
                      variant="outlined"
                      component="a"
                      href={product.ProductUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      icon={<OpenInNewOutlinedIcon />}
                      label="Supplier"
                      sx={{
                        height: 24,
                        borderRadius: 1,
                        fontSize: "0.72rem",
                        fontWeight: 500,
                        borderColor: "divider",
                        color: "text.secondary",
                        "& .MuiChip-label": { px: "8px" },
                        "&:hover": {
                          borderColor: "primary.main",
                          color: "primary.main",
                        },
                        transition: "all 0.15s ease",
                      }}
                    />
                  </Tooltip>
                )}
              </Stack>
            )}
          </Box>

          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{ flexShrink: 0, mt: 0.25 }}
          >
            <Tooltip
              title={isFullscreen ? "Restore size" : "Expand to fullscreen"}
              placement="bottom"
            >
              <IconButton
                size="small"
                onClick={() => setIsFullscreen((v) => !v)}
                aria-label={
                  isFullscreen ? "Exit fullscreen" : "Enter fullscreen"
                }
                sx={{ color: "text.secondary" }}
              >
                {isFullscreen ? (
                  <FullscreenExitOutlinedIcon sx={{ fontSize: 18 }} />
                ) : (
                  <FullscreenOutlinedIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </Tooltip>

            <Divider
              orientation="vertical"
              flexItem
              sx={{ mx: 0.25, height: 20, alignSelf: "center" }}
            />

            <Tooltip title="Close" placement="bottom">
              <IconButton
                size="small"
                onClick={() => safeClose()}
                aria-label="Close product details"
                sx={{
                  color: "text.secondary",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.error.main, 0.08),
                    color: "error.main",
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>
    );
  };

  const renderFooter = () => (
    <Box
      sx={{
        px: { xs: 2, md: 3 },
        py: 1.5,
        borderTop: "1px solid",
        borderColor: "divider",
        bgcolor: alpha(theme.palette.background.paper, 0.97),
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          {!readOnly && lookupType !== "custom" && (
            <Button
              variant="contained"
              startIcon={
                syncing ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <SyncOutlinedIcon />
                )
              }
              onClick={handleSyncProduct}
              disabled={syncing || loading || !product}
              sx={{ textTransform: "none", fontWeight: 700, height: 36 }}
            >
              {syncing ? "Syncing…" : "Sync Product"}
            </Button>
          )}

          {!readOnly && lookupType === "custom" && (
            <Button
              variant="outlined"
              color="error"
              startIcon={
                deleting ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <DeleteOutlineOutlinedIcon />
                )
              }
              onClick={handleDelete}
              disabled={deleting || loading || !product?.id}
              sx={{ textTransform: "none", fontWeight: 600, height: 36 }}
            >
              {deleting ? "Deactivating…" : "Set as Inactive"}
            </Button>
          )}

          {product && (
            <Stack direction="row" spacing={0.25}>
              {safeStr(product?.ProductUrl) && (
                <Tooltip title="Open on supplier site" placement="top">
                  <IconButton
                    size="small"
                    component="a"
                    href={product.ProductUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: "text.secondary" }}
                  >
                    <OpenInNewOutlinedIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
              )}

              {/* {productNumber && (
                <Tooltip title="Copy part number" placement="top">
                  <IconButton
                    size="small"
                    onClick={() => handleCopy(productNumber)}
                    sx={{ color: "text.secondary" }}
                  >
                    <ContentCopyOutlinedIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
              )} */}
            </Stack>
          )}
        </Stack>

        <Button
          variant="text"
          onClick={() => safeClose()}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            color: "text.secondary",
          }}
        >
          Close
        </Button>
      </Stack>
    </Box>
  );

  return (
    <>
      <Dialog
        open={Boolean(isOpen)}
        onClose={() => safeClose()}
        fullWidth
        fullScreen={isFullscreen}
        maxWidth="xl"
        scroll="paper"
        aria-labelledby="product-popup-title"
        PaperProps={{
          sx: {
            borderRadius: isFullscreen ? 0 : 2,
            overflow: "hidden",
            bgcolor: "background.default",
            backgroundImage: "none",
            borderTop: isFullscreen ? "none" : "3px solid",
            borderTopColor: "primary.main",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {renderHeader()}

        <DialogContent
          id="product-popup-title"
          dividers={false}
          sx={{
            p: 0,
            flex: 1,
            overflow: "auto",
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: alpha(theme.palette.text.disabled, 0.25),
              borderRadius: 99,
            },
            "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
          }}
        >
          {renderContent()}
        </DialogContent>

        {renderFooter()}
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={1800}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ borderRadius: 2, fontSize: "0.8125rem", alignItems: "center" }}
          icon={<ContentCopyOutlinedIcon fontSize="inherit" />}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default ProductPopup;
